import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Phone, Check, Clock, Shield, Users, FileText, MapPin, MessageSquare, BadgeCheck } from "lucide-react";
import Header from "@/components/sections/Header";
import Footer from "@/components/sections/Footer";
import CalcForm from "@/components/sections/CalcForm";
import { useSeo } from "@/lib/useSeo";
import { trackEvent } from "@/lib/analytics";

const HERO_IMG =
  "https://images.unsplash.com/photo-1770425910016-4b1b45991db9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBlbXB0eSUyMG9mZmljZSUyMGludGVyaW9yJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc3NjcxMzY3M3ww&ixlib=rb-4.1.0&q=85";

const INCLUDED = [
  "Vysávání koberců a čalouněného nábytku",
  "Mytí podlah (vinyl, dlažba, vinyl-click, koberce)",
  "Úklid sociálních zařízení a sanitárního servisu",
  "Úklid firemní kuchyňky a zázemí pro zaměstnance",
  "Vynesení odpadu a třídění",
  "Dezinfekce kontaktních ploch (kliky, vypínače, zábradlí, klávesnice)",
  "Otírání ploch, stolů, monitorů a pracovních pultů",
  "Mytí zrcadel, skla a interiérových prosklení",
  "Doplnění hygienických prostředků (toaletní papír, mýdlo, ručníky)",
  "Údržba zasedacích místností a recepce",
];

const PROCESS = [
  {
    step: "01",
    title: "Nezávazná konzultace",
    desc: "Spojíme se s vámi do 24 hodin a probereme váš provoz, požadavky a očekávaný standard čistoty.",
  },
  {
    step: "02",
    title: "Prohlídka kanceláře",
    desc: "Přijedeme k vám do kanceláře, zaměříme plochy, typy povrchů a frekvenci využití prostor.",
  },
  {
    step: "03",
    title: "Návrh řešení a kalkulace",
    desc: "Připravíme harmonogram úklidu a cenovou nabídku přesně na míru vašemu provozu.",
  },
  {
    step: "04",
    title: "Nasazení úklidového týmu",
    desc: "Stálý prověřený tým začne pracovat podle schváleného standardu — zpravidla do 48 hodin od smlouvy.",
  },
  {
    step: "05",
    title: "Pravidelná kontrola kvality",
    desc: "Manažer pravidelně audituje výsledky a reaguje na vaši zpětnou vazbu. Reklamace řešíme na místě.",
  },
];

const PRICING_FACTORS = [
  {
    title: "Velikost kanceláře",
    desc: "Plocha v m² a počet samostatných místností přímo ovlivňují čas úklidu a nasazený tým.",
  },
  {
    title: "Frekvence úklidu",
    desc: "Denní úklid je dlouhodobě výhodnější než nepravidelné jednorázové zásahy. Sazba začíná od 250 Kč/hodinu.",
  },
  {
    title: "Počet zaměstnanců",
    desc: "Vyšší obsazenost znamená intenzivnější využití sociálního zařízení, kuchyňky a společných ploch.",
  },
  {
    title: "Rozsah služeb",
    desc: "Standardní úklid, doplnění hygieny, mytí oken zvenčí nebo hloubkové čištění — vše domlouváme samostatně.",
  },
];

const WHY = [
  {
    Icon: Clock,
    title: "Mimo provozní dobu",
    desc: "Uklízíme brzy ráno, večer nebo o víkendu — chod kanceláře nenarušíme.",
  },
  {
    Icon: Shield,
    title: "Diskrétnost a NDA",
    desc: "Citlivá firemní data ve vašem open-space jsou v bezpečí. Standardně podepisujeme NDA.",
  },
  {
    Icon: MessageSquare,
    title: "Rychlá komunikace",
    desc: "Žádné call-centrum. Píšete přímo manažerovi, který váš objekt zná.",
  },
  {
    Icon: BadgeCheck,
    title: "Kontrola kvality",
    desc: "Pravidelný audit úklidu a aktivní práce s vaší zpětnou vazbou.",
  },
  {
    Icon: FileText,
    title: "Fakturace firmám",
    desc: "Měsíční fakturace, splatnost na míru, vše hladce pro vaše účetní.",
  },
  {
    Icon: MapPin,
    title: "Lokální Brno tým",
    desc: "Sídlíme i pracujeme v Brně. Krátká reakční doba pro firmy v Brně a okolí.",
  },
];

const FAQ = [
  {
    q: "Jaká je cena úklidu kanceláří v Brně?",
    a: "Cena úklidu kanceláří se odvíjí od rozsahu a frekvence úklidu, velikosti prostor a počtu zaměstnanců. Naše sazba začíná od 250 Kč/hodinu. Přesnou kalkulaci připravíme po bezplatné prohlídce — kontaktujte nás a ozveme se do 24 hodin.",
  },
  {
    q: "Kdy provádíte úklid kanceláří?",
    a: "Čas úklidu přizpůsobujeme provozu vaší firmy. Nabízíme ranní úklid kanceláří před otevřením, úklid v průběhu pracovního dne i večerní úklid po skončení provozu, případně o víkendu.",
  },
  {
    q: "Uklízíte i administrativní budovy a kancelářské budovy v Brně?",
    a: "Ano, úklid administrativních budov a vícepodlažních kancelářských budov je naší specializací. Zajišťujeme úklid všech společných prostor — recepce, výtahy, schodiště, chodby — i jednotlivých kanceláří.",
  },
  {
    q: "Co zahrnuje pravidelný úklid kanceláří?",
    a: "Standardní rozsah obsahuje vysávání, mytí podlah, úklid kuchyňky a sociálních zařízení, dezinfekci kontaktních ploch, vynesení odpadu, doplnění hygienických prostředků a údržbu zasedacích místností. Konkrétní rozsah ladíme individuálně podle vašich potřeb.",
  },
  {
    q: "Jak rychle nastoupíte?",
    a: "Jsme schopni nastoupit zpravidla do 48 hodin od podpisu smlouvy. V akutních případech (například po malování, stěhování) umíme nasadit tým ještě dříve — domluvte si volný termín telefonicky.",
  },
  {
    q: "Nabízíte i komerční úklid pro provozovny a obchody?",
    a: "Ano, kromě kanceláří zajišťujeme komerční úklid provozoven, výrobních hal, skladů a obchodů. Podrobnosti najdete na stránce úklid provozoven v Brně.",
  },
  {
    q: "Podepisujete NDA / dohodu o mlčenlivosti?",
    a: "Ano, NDA podepisujeme standardně. Pracujeme s firemními daty a otevřenými dokumenty každý den — diskrétnost a profesionální chování týmu je u nás samozřejmostí.",
  },
  {
    q: "Co když dojde k reklamaci úklidu?",
    a: "Reklamace řešíme přímo na místě s objednavatelem do 24 hodin. Manažer pravidelně audituje kvalitu a případné připomínky napravíme okamžitě, bez doplatků.",
  },
  {
    q: "Uklízíte i v okolí Brna?",
    a: "Ano, působíme i v okolí Brna — uklízíme například v Modřicích, Šlapanicích, Kuřimi a dalších obcích v blízkosti Brna. V případě dotazu nás kontaktujte.",
  },
];

const UklidKancelariBrno = () => {
  useSeo({
    title:
      "Úklid kanceláří Brno | Pravidelný a komerční úklid firem | SANVIORO",
    description:
      "Úklid kanceláří v Brně — pravidelný úklid firemních prostor, administrativních a kancelářských budov. Stálý tým, NDA, fakturace firmám. Nabídka do 24 hodin.",
    path: "/uklid-kancelari-brno",
  });

  // Inline JSON-LD for LocalBusiness + FAQ — boosts SERP appearance
  const ldJson = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "LocalBusiness",
        "@id": "https://sanvioro.cz/#business",
        name: "SANVIORO",
        legalName: "Sanvioro s.r.o.",
        description: "Profesionální úklid kanceláří, firem a objektů v Brně a okolí.",
        telephone: "+420775601254",
        email: "info@sanvioro.cz",
        url: "https://sanvioro.cz/",
        areaServed: { "@type": "City", name: "Brno" },
        address: {
          "@type": "PostalAddress",
          streetAddress: "Příkop 843/4",
          addressLocality: "Brno",
          postalCode: "60200",
          addressCountry: "CZ",
        },
      },
      {
        "@type": "Service",
        name: "Úklid kanceláří v Brně",
        provider: { "@id": "https://sanvioro.cz/#business" },
        areaServed: { "@type": "City", name: "Brno" },
        serviceType: "Pravidelný úklid kanceláří",
        url: "https://sanvioro.cz/uklid-kancelari-brno",
      },
      {
        "@type": "FAQPage",
        mainEntity: FAQ.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };

  return (
    <div className="App font-body text-[#111111] bg-white" data-testid="uklid-kancelari-page">
      <Header />

      {/* JSON-LD structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(ldJson) }}
      />

      {/* HERO */}
      <section className="relative min-h-[88svh] overflow-hidden bg-[#F5F5F7]">
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="Moderní kancelář v Brně po profesionálním úklidu SANVIORO"
            className="w-full h-full object-cover"
            style={{ filter: "brightness(1.22) contrast(0.92) saturate(0.95)" }}
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(to bottom, rgba(46,52,60,0.42) 0%, rgba(46,52,60,0.28) 45%, rgba(30,36,44,0.62) 100%)",
            }}
          />
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "radial-gradient(ellipse at 78% 18%, rgba(27,147,164,0.18) 0%, rgba(27,147,164,0) 55%)",
            }}
          />
        </div>
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.08]"
          style={{
            backgroundImage:
              "linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)",
            backgroundSize: "80px 80px",
          }}
        />

        <div className="relative mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20 pt-32 md:pt-40 pb-16 md:pb-24 flex flex-col">
          <div className="text-white/85 text-xs uppercase tracking-[0.28em] font-semibold">
            Brno · pravidelný úklid kanceláří
          </div>

          <h1
            data-testid="page-h1"
            className="mt-10 max-w-4xl font-display font-black text-white text-[clamp(2.5rem,7vw,6rem)] leading-[0.95] tracking-[-0.03em]"
          >
            Úklid kanceláří
            <span className="block italic text-[#1B93A4]">v Brně.</span>
          </h1>

          <p className="mt-8 max-w-2xl text-white text-[18px] md:text-[22px] leading-[1.75] font-light">
            Pravidelný profesionální úklid pro kanceláře, open-space, zasedací
            místnosti a recepce. Diskrétně, spolehlivě a podle vašeho rozvrhu.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href="#kalkulace"
              data-testid="hero-cta"
              onClick={() =>
                trackEvent("cta_click_spocitat_uklid", { location: "uklid_kancelari_hero" })
              }
              className="group inline-flex items-center gap-3 bg-white text-[#111111] px-7 h-14 font-semibold tracking-tight hover:bg-[#0F5C69] hover:text-white transition-colors"
            >
              Získat kalkulaci
              <ArrowRight
                size={18}
                strokeWidth={2.5}
                className="transition-transform group-hover:translate-x-1"
              />
            </a>
            <a
              href="tel:+420775601254"
              className="inline-flex items-center gap-3 bg-transparent text-white border border-white/40 px-7 h-14 font-semibold tracking-tight hover:bg-white/10 transition-colors"
            >
              <Phone size={16} strokeWidth={2.5} />
              <span className="tabular-nums">+420 775 601 254</span>
            </a>
          </div>
        </div>
      </section>

      {/* CO ZAHRNUJE */}
      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20">
            <div className="lg:col-span-5">
              <div className="text-xs uppercase tracking-[0.28em] font-bold text-[#0F5C69]">
                [ 01 ] — Rozsah služby
              </div>
              <h2 className="font-display text-4xl md:text-6xl font-bold tracking-[-0.025em] leading-[1.02] mt-5">
                Co zahrnuje pravidelný<br />úklid kanceláří.
              </h2>
              <p className="mt-8 text-[#555555] leading-relaxed text-base md:text-lg max-w-md">
                Standardní rozsah komerčního úklidu pro kanceláře, open-space
                a administrativní budovy v Brně. Specifické požadavky — mytí
                oken zvenčí, hloubkové čištění koberců, dezinfekce po nemoci —
                domlouváme individuálně.
              </p>
            </div>
            <div className="lg:col-span-7 lg:pl-8 lg:border-l border-[#E5E5E5]">
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                {INCLUDED.map((it) => (
                  <li
                    key={it}
                    className="flex items-start gap-3 text-[15px] text-[#222222] leading-relaxed"
                  >
                    <Check
                      size={18}
                      strokeWidth={2.5}
                      className="text-[#0F5C69] shrink-0 mt-1"
                    />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* JAK PROBÍHÁ ÚKLID KANCELÁŘÍ */}
      <section
        data-testid="process-section"
        className="bg-[#F5F5F7] py-16 md:py-20"
      >
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.28em] font-bold text-[#0F5C69]">
              [ 02 ] — Proces
            </div>
            <h2 className="font-display text-4xl md:text-6xl font-bold tracking-[-0.025em] leading-[1.02] mt-5">
              Jak probíhá<br />úklid kanceláří.
            </h2>
            <p className="mt-6 text-[#555555] leading-relaxed text-base md:text-lg">
              Od první konzultace po pravidelnou kontrolu kvality. Celý proces
              vedeme pro vás v Brně transparentně a s jedním kontaktním
              manažerem.
            </p>
          </div>
          <ol className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 md:gap-8">
            {PROCESS.map((s, i) => (
              <li
                key={s.step}
                data-testid={`process-card-${s.step}`}
                className="relative border-t border-[#0F5C69]/30 pt-6"
              >
                <div className="font-display text-5xl font-black text-[#E6F2F4] leading-none tracking-tight">
                  {s.step}
                </div>
                <h3 className="font-display text-lg md:text-xl font-bold tracking-tight mt-4 text-[#111111]">
                  {s.title}
                </h3>
                <p className="mt-3 text-[#555555] leading-relaxed text-[14px]">
                  {s.desc}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* JAK STANOVUJEME CENU ÚKLIDU */}
      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20">
            <div className="lg:col-span-5">
              <div className="text-xs uppercase tracking-[0.28em] font-bold text-[#0F5C69]">
                [ 03 ] — Cenotvorba
              </div>
              <h2 className="font-display text-4xl md:text-6xl font-bold tracking-[-0.025em] leading-[1.02] mt-5">
                Jak stanovujeme<br />cenu úklidu.
              </h2>
              <p className="mt-8 text-[#555555] leading-relaxed text-base md:text-lg max-w-md">
                Cena úklidu kanceláří v Brně se odvíjí od čtyř hlavních
                faktorů. Po bezplatné prohlídce dostanete jasnou cenovou
                nabídku — bez skrytých poplatků a s fakturací firmám.
              </p>
              <div className="mt-8 border-l-2 border-[#0F5C69] pl-5">
                <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#0F5C69]">
                  Orientační sazba
                </div>
                <div className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1 text-[#111111]">
                  od 250 Kč / hodinu
                </div>
                <p className="mt-2 text-[13px] text-[#666666]">
                  Konkrétní cena je vždy individuální dle objektu.
                </p>
              </div>
            </div>
            <div className="lg:col-span-7 lg:pl-8 lg:border-l border-[#E5E5E5]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                {PRICING_FACTORS.map((p, i) => (
                  <div
                    key={p.title}
                    data-testid={`pricing-factor-${i}`}
                    className="border-t border-[#E5E5E5] pt-6"
                  >
                    <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#0F5C69]">
                      0{i + 1}
                    </div>
                    <h3 className="font-display text-xl md:text-2xl font-bold tracking-tight mt-2 text-[#111111]">
                      {p.title}
                    </h3>
                    <p className="mt-3 text-[#555555] leading-relaxed text-[15px]">
                      {p.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROČ FIRMY V BRNĚ VOLÍ SANVIORO */}
      <section className="bg-[#F5F5F7] py-16 md:py-20">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.28em] font-bold text-[#0F5C69]">
              [ 04 ] — Proč my
            </div>
            <h2 className="font-display text-4xl md:text-6xl font-bold tracking-[-0.025em] leading-[1.02] mt-5">
              Proč firmy v Brně<br />volí SANVIORO.
            </h2>
            <p className="mt-6 text-[#555555] leading-relaxed text-base md:text-lg">
              Šest konkrétních důvodů, proč nás vybírají kanceláře,
              administrativní budovy a provozovny po celém Brně.
            </p>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E5E5E5] border border-[#E5E5E5]">
            {WHY.map((w, i) => (
              <div
                key={w.title}
                data-testid={`why-card-${i}`}
                className="bg-white p-7 md:p-8 min-h-[230px] flex flex-col"
              >
                <div className="w-12 h-12 rounded-xl bg-[#0F5C69] flex items-center justify-center shadow-sm">
                  <w.Icon
                    size={22}
                    strokeWidth={2}
                    className="text-white"
                  />
                </div>
                <h3 className="font-display text-xl md:text-2xl font-bold tracking-tight mt-6">
                  {w.title}
                </h3>
                <p className="mt-3 text-[#555555] leading-relaxed text-[14px]">
                  {w.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* KALKULACE — re-uses CalcForm; trim bottom padding so FAQ flows directly */}
      <div id="kalkulace" className="-mb-12 md:-mb-16">
        <CalcForm />
      </div>

      {/* FAQ */}
      <section className="bg-white pt-4 md:pt-6 pb-16 md:pb-20">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="text-xs uppercase tracking-[0.28em] font-bold text-[#0F5C69]">
                [ 05 ] — Časté otázky
              </div>
              <h2 className="font-display text-4xl md:text-6xl font-bold tracking-[-0.025em] leading-[1.02] mt-5">
                Co se nás<br />klienti ptají.
              </h2>
              <p className="mt-8 text-[#555555] leading-relaxed max-w-md">
                Něco nenacházíte? Napište nám na{" "}
                <a
                  href="mailto:info@sanvioro.cz"
                  className="text-[#0F5C69] underline decoration-[#0F5C69]/40 underline-offset-4 hover:decoration-[#0F5C69]"
                >
                  info@sanvioro.cz
                </a>{" "}
                — odpovíme do 24 hodin.
              </p>
            </div>
            <div className="lg:col-span-7">
              <div className="divide-y divide-[#E5E5E5] border-y border-[#E5E5E5]">
                {FAQ.map((f, i) => (
                  <details
                    key={f.q}
                    data-testid={`faq-${i}`}
                    className="group py-6 cursor-pointer"
                  >
                    <summary className="flex justify-between items-start gap-4 list-none">
                      <span className="font-display text-lg md:text-xl font-semibold tracking-tight text-[#111111]">
                        {f.q}
                      </span>
                      <span className="shrink-0 text-2xl font-light text-[#0F5C69] transition-transform group-open:rotate-45">
                        +
                      </span>
                    </summary>
                    <p className="mt-4 text-[#555555] leading-relaxed text-[15px] pr-8">
                      {f.a}
                    </p>
                  </details>
                ))}
              </div>
              <div className="mt-8">
                <a
                  data-testid="reviews-google-link"
                  href="https://g.page/r/CX0iCsN9Py7SEAE/review"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    trackEvent("cta_click_google_reviews", {
                      location: "uklid_kancelari_faq",
                    })
                  }
                  className="group inline-flex items-center gap-2 font-display text-lg md:text-xl font-semibold tracking-tight text-[#0F5C69] hover:gap-3 transition-all"
                >
                  Přečtěte si, co říkají naši klienti na Google
                  <ArrowRight
                    size={18}
                    strokeWidth={2.5}
                    className="transition-transform group-hover:translate-x-1"
                  />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INTERNÍ PROLINKOVÁNÍ */}
      <section className="bg-[#0F5C69] text-white py-16 md:py-20">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <div>
            <div className="text-xs uppercase tracking-[0.28em] font-bold text-white/70">
              Další služby SANVIORO
            </div>
            <h3 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-3 max-w-xl">
              Nestačí jen kanceláře?
            </h3>
            <p className="mt-4 text-white/85 max-w-xl leading-relaxed">
              Uklízíme i bytové domy, společné prostory a spravujeme objekty
              po celém Brně. Více informací najdete na hlavní stránce nebo nás
              kontaktujte přímo.
            </p>
          </div>
          <div className="flex flex-wrap gap-4 shrink-0">
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-white text-[#0F5C69] px-6 h-12 font-semibold hover:bg-[#E6F2F4] transition-colors"
            >
              Hlavní stránka
            </Link>
            <Link
              to="/o-nas"
              className="inline-flex items-center gap-2 border border-white/40 text-white px-6 h-12 font-semibold hover:bg-white/10 transition-colors"
            >
              O nás
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default UklidKancelariBrno;
